// src/components/common/PincodeInput.js - FIXED for Pan-India
import React, { useState, useEffect } from "react";
import {
  MapPin,
  CheckCircle,
  AlertCircle,
  Loader,
  Truck,
  Clock,
} from "lucide-react";
import { pincodeAPI } from "../../services/api";
import "./PincodeInput.css";

const PincodeInput = ({
  value,
  onChange,
  onAutofill,
  error,
  disabled = false,
  required = false,
  showDeliveryInfo = false,
}) => {
  const [validating, setValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  const [validationMessage, setValidationMessage] = useState("");
  const [pincodeData, setPincodeData] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [whatsappLink, setWhatsappLink] = useState(null);

  useEffect(() => {
    if (/^[1-9][0-9]{5}$/.test(value)) {
      validatePincode(value);
    } else {
      resetValidation();
    }
  }, [value]);

  const resetValidation = () => {
    setValidationStatus(null);
    setValidationMessage("");
    setWhatsappLink(null);
    setPincodeData(null);
    setDeliveryInfo(null);
  };

  const validatePincode = async (pincode) => {
    try {
      setValidating(true);

      // 1️⃣ Validate PIN
      const response = await pincodeAPI.validatePincode(pincode);

      if (!response.data.success) {
        throw new Error("Invalid PIN");
      }

      setValidationStatus("valid");
      setValidationMessage(response.data.message);
      setPincodeData(response.data.data);

      let deliveryData = null;

      // 2️⃣ Fetch delivery info ONLY once
      if (showDeliveryInfo) {
        const deliveryRes = await pincodeAPI.getDeliveryInfo(pincode);
        if (deliveryRes.data.success) {
          const info = deliveryRes.data.data ?? deliveryRes.data;

          deliveryData = info;
          setDeliveryInfo(info);
        }
      }

      // 3️⃣ 🚀 SEND FULL DATA UP
      if (onAutofill) {
        onAutofill({
          city: response.data.data.district || response.data.data.city,
          state: response.data.data.state,
          country: "India",
          deliveryCharge: deliveryData?.deliveryCharge ?? null,
          estimatedDays: deliveryData?.estimatedDays ?? null,
        });
      }
    } catch (error) {
      const errorData = error.response?.data;
      setValidationStatus("invalid");
      setValidationMessage(errorData?.message || "Invalid PIN code");
      setWhatsappLink(errorData?.whatsappLink || null);
      setPincodeData(null);
      setDeliveryInfo(null);
    } finally {
      setValidating(false);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value.replace(/\D/g, "").slice(0, 6);
    onChange(newValue);
  };

  return (
    <div className="pincode-input-wrapper">
      <div className={`pincode-input-container ${validationStatus || ""}`}>
        <MapPin size={18} className="pincode-icon" />
        <input
          type="text"
          value={value}
          onChange={handleChange}
          placeholder="Enter 6-digit PIN code"
          maxLength="6"
          disabled={disabled}
          required={required}
          className={`pincode-input ${error ? "error" : ""}`}
        />

        <div className="validation-icon">
          {validating && <Loader size={16} className="spinning" />}
          {!validating && validationStatus === "valid" && (
            <CheckCircle size={16} className="valid-icon" />
          )}
          {!validating && validationStatus === "invalid" && (
            <AlertCircle size={16} className="invalid-icon" />
          )}
        </div>
      </div>

      {/* Validation Message */}
      {validationMessage && !error && (
        <p className={`validation-message ${validationStatus}`}>
          {validationMessage}
        </p>
      )}

      {/* WhatsApp CTA for out-of-state users */}
      {validationStatus === "invalid" && whatsappLink && (
        <div className="whatsapp-cta">
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-btn"
          >
            💬 Order via WhatsApp
          </a>
        </div>
      )}

      {/* Error from parent */}
      {error && <p className="validation-message invalid">{error}</p>}

      {/* Location Details */}
      {pincodeData && validationStatus === "valid" && (
        <div className="pincode-details">
          <p className="location-info">
            <MapPin size={14} />
            <span>
              <strong>Location:</strong>{" "}
              {pincodeData.district || pincodeData.city}, {pincodeData.state}
            </span>
          </p>
          {pincodeData.postOffices && pincodeData.postOffices.length > 0 && (
            <p className="post-office-info">
              <strong>Post Office:</strong> {pincodeData.postOffices[0].name}
            </p>
          )}
        </div>
      )}

      {/* Delivery Information */}
      {deliveryInfo && validationStatus === "valid" && (
        <div className="delivery-info">
          <div className="delivery-item">
            <Truck size={14} />
            <span>Delivery: {deliveryInfo.estimatedDays} days</span>
          </div>
          <div className="delivery-item">
            <span>
              Charge:{" "}
              {deliveryInfo.deliveryCharge === 0
                ? "Free"
                : `₹${deliveryInfo.deliveryCharge}`}
            </span>
          </div>
        </div>
      )}

      {/* Pan-India Service Info */}
      {!value && (
        <p className="service-info">
          <MapPin size={12} />
          <span>We deliver across India</span>
        </p>
      )}
    </div>
  );
};

export default PincodeInput;
